<template>
  <div
    v-if="showBanner"
    class="w-full bg-surface md:px-10 rounded-2xl border-2 border-primary flex flex-col md:flex-row gap-4 px-8 justify-between items-center"
    :class="[completedContents.totalCount === 0 ? 'py-6' : 'py-3']"
  >
    <div class="flex gap-2">
      <icon :icon="SuccessSvg" class="text-success min-w-6" />
      <span>
        <strong>{{ completedContents.totalCount }}</strong>
        {{ $t('completed-contents-banner.of') }}
        <strong>{{ contents.totalCount }}</strong>
        {{ $t('completed-contents-banner.contents-completed') }}
      </span>
    </div>
    <Btn
      v-if="completedContents.totalCount !== 0"
      :variant="BtnVariant.FILLED"
      :color="BtnColor.PRIMARY"
      :size="BtnSize.LG"
      class="w-full md:w-fit"
      @click="handleSubmit"
      >{{
        isRemainingContentFilterApplied
          ? $t('completed-contents-banner.view-all')
          : $t('completed-contents-banner.view-remaining')
      }}</Btn
    >
  </div>
</template>
<script setup lang="ts">
import { Btn, BtnColor, BtnSize, BtnVariant } from '~/components/btn';
import SuccessSvg from '@/assets/icons/success.svg?component';
import { Icon } from '~/components/icon';
import { HubFilters } from '~/pages/_components/hub-details/_types';
import { apiGetContentsList } from '~/api/contents';
import { HubDto } from '~/api/hubs';
import { LaunchMetricType } from '~/api/metrics';
import { storeToRefs } from 'pinia';
import { useAccountStore } from '~/store/account';
import { AccountType } from '~/api/accounts';
import { useAuthUserStore } from '~/store/auth-user';

type Props = {
  hub: HubDto;
};

const props = defineProps<Props>();
const filters = defineModel<HubFilters>({ required: true });

// TODO migrate to useAsyncData
const [completedContents, contents] = await Promise.all([
  apiGetContentsList(props.hub.slug, {
    page: 1,
    perPage: 1,
    type: [],
    launchMetricTypes: [LaunchMetricType.COMPLETED],
  }),
  apiGetContentsList(props.hub.slug, {
    page: 1,
    perPage: 1,
    type: [],
  }),
]);

const { currentAccount } = storeToRefs(useAccountStore());

const showBanner = computed(
  () =>
    currentAccount.value?.type === AccountType.STANDARD &&
    props.hub.isLaunchEnabled,
);

const REMAINING_CONTENT_METRICS = [
  LaunchMetricType.NOT_STARTED,
  LaunchMetricType.OPENED,
];

const isRemainingContentFilterApplied = computed(
  () =>
    filters.value.status.length === REMAINING_CONTENT_METRICS.length &&
    filters.value.status.every((metric) =>
      REMAINING_CONTENT_METRICS.includes(metric),
    ),
);

const viewRemainingContent = () => {
  filters.value.status = REMAINING_CONTENT_METRICS;
};

const showAllContents = () => {
  filters.value.status = [];
};

const handleSubmit = () =>
  isRemainingContentFilterApplied.value
    ? showAllContents()
    : viewRemainingContent();
</script>
